import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  LinearProgress,
  IconButton,
  Grid,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { createSong, updateSong } from '../utils/SongsAPIs';

const SongModal = ({ isOpen, onClose, songToEdit, selectedSong, onSuccess }) => {
  const [name, setName] = useState('');
  const [copyrightLink, setCopyrightLink] = useState('');
  const [cover, setCover] = useState(null);
  const [audio, setAudio] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [audioPreview, setAudioPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const activeSong = songToEdit || selectedSong;
  const isEditMode = Boolean(activeSong);

  useEffect(() => {
    if (activeSong) {
      setName(activeSong.name || '');
      setCopyrightLink(activeSong.copyrightLink || '');
      setCoverPreview(activeSong.coverUrl || null);
      setAudioPreview(activeSong.audioUrl || null);
    } else {
      setName('');
      setCopyrightLink('');
      setCoverPreview(null);
      setAudioPreview(null);
    }

    setCover(null);
    setAudio(null);
  }, [activeSong, isOpen]);

  const handleCoverChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setCover(file);
      setCoverPreview(URL.createObjectURL(file));
    }
  };

  const handleAudioChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setAudio(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim()) {
      alert('Song name is required.');
      return;
    }

    const formData = new FormData();
    formData.append('name', name);
    formData.append('copyrightLink', copyrightLink);
    if (cover) formData.append('cover', cover);
    if (audio) formData.append('audio', audio);

    try {
      setLoading(true);

      if (isEditMode) {
        const songId = activeSong._id || activeSong.id;
        if (!songId) throw new Error('Song ID is missing');
        await updateSong(songId, formData);
      } else {
        await createSong(formData);
      }

      if (typeof onSuccess === 'function') {
        await onSuccess();
      }

      onClose();
    } catch (err) {
      console.error('Failed to save song:', err);
      alert('Error processing request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, p: 1.5, maxWidth: '520px' } }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, pt: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
            {isEditMode ? 'Edit Song' : 'Upload Song'}
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            {isEditMode ? 'Update existing song details' : 'Upload copyright free song'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 3, py: 2 }}>
        <form onSubmit={handleSubmit}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block', color: '#334155' }}>
              Song Name <Box component="span" sx={{ color: 'red' }}>*</Box>
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. Baby live"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              variant="outlined"
              sx={{ backgroundColor: '#f8fafc' }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block', color: '#334155' }}>
              Copy right link
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="https://..."
              value={copyrightLink}
              onChange={(e) => setCopyrightLink(e.target.value)}
              variant="outlined"
              sx={{ backgroundColor: '#f8fafc' }}
            />
            <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '11px', mt: 0.5, display: 'block' }}>
              Used for compliance purpose
            </Typography>
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#334155', fontSize: '13px' }}>
                Upload Song Cover
              </Typography>
              <Box
                component="label"
                sx={{
                  border: '1.5px dashed #c084fc',
                  borderRadius: 2,
                  p: 2,
                  height: 180,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  backgroundColor: '#f8fafc',
                  backgroundImage: coverPreview ? `url(${coverPreview})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  '&:hover': { borderColor: '#a855f7' },
                }}
              >
                <input type="file" accept="image/png, image/jpeg, image/webp" hidden onChange={handleCoverChange} />
                {!coverPreview && (
                  <>
                    <AddIcon
                      sx={{
                        fontSize: 32,
                        background: 'linear-gradient(135deg, #8b5cf6 0%, #d946ef 50%, #f43f5e 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        display: 'inline-block',
                      }}
                    />
                    <Typography variant="caption" sx={{ color: '#94a3b8', mt: 1, fontSize: '11px', textAlign: 'center' }}>
                      PNG, JPG, Max 30MB
                    </Typography>
                  </>
                )}
              </Box>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#334155', fontSize: '13px' }}>
                Upload Song
              </Typography>
              <Box
                component="label"
                sx={{
                  border: '1.5px dashed #c084fc',
                  borderRadius: 2,
                  p: 2,
                  height: 120,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  backgroundColor: '#f8fafc',
                  '&:hover': { borderColor: '#a855f7' },
                }}
              >
                <input type="file" accept="audio/*" hidden onChange={handleAudioChange} />
                {audio ? (
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: '#7e22ce', textAlign: 'center', wordBreak: 'break-all' }}
                  >
                    🎵 {audio.name}
                  </Typography>
                ) : audioPreview ? (
                  <Box sx={{ textAlign: 'center', px: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#7e22ce' }}>
                      🎵 {audioPreview.split('/').pop()}
                    </Typography>
                  </Box>
                ) : (
                  <AddIcon
                    sx={{
                      fontSize: 32,
                      background: 'linear-gradient(135deg, #8b5cf6 0%, #d946ef 50%, #f43f5e 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      display: 'inline-block',
                    }}
                  />
                )}
              </Box>

              {loading && (
                <LinearProgress
                  variant="determinate"
                  value={60}
                  sx={{
                    mt: 1.5,
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: '#e2e8f0',
                    '& .MuiLinearProgress-bar': { backgroundColor: '#60a5fa' },
                  }}
                />
              )}

              <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '11px', mt: 0.5, display: 'block' }}>
                PNG, JPG, Max 30MB
              </Typography>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3 }}>
            <Button onClick={onClose} sx={{ color: '#64748b', textTransform: 'none', fontWeight: 600 }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                backgroundColor: '#2563eb',
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                '&:hover': { backgroundColor: '#1d4ed8' },
              }}
            >
              {isEditMode ? 'Update Song' : 'Add Song'}
            </Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SongModal;
